import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { ApiError, errorMessage, get, post } from '../api/client';
import type { Publication, PublicationStatus, PublishCounts, PublishIssue, PublishStatus } from '../api/types';
import { useMe } from '../auth';
import { IconAlert, IconCheck, IconExternal, IconRotate } from '../components/icons';
import { formatDate, PageHead, Spinner, timeAgo, useFeedback } from '../components/ui';
import { tk, type Translator, useT } from '../i18n';

/** Las etiquetas se traducen con t() al mostrarlas. */
const STATUS: Record<PublicationStatus, { label: string; tone: string }> = {
  PENDING: { label: tk('Preparando'), tone: 'info' },
  BUILDING: { label: tk('Publicando'), tone: 'info' },
  SUCCEEDED: { label: tk('Publicada'), tone: 'ok' },
  FAILED: { label: tk('Falló'), tone: 'red' },
};

/** Sección del editor de la moto donde se arregla cada cosa. */
const SECTION: Record<string, string> = { general: '', ficha: 'ficha', colores: 'colores', especiales: 'especiales', galeria: 'galeria' };

export type PublishSite = 'mtx' | 'phelon-moore';

/** Lo que cambia entre publicar MTX y publicar Phelon & Moore. Los textos se traducen con t() al mostrarlos. */
interface SiteConfig {
  /** Rutas de la API y claves de caché de React Query. */
  api: string;
  key: string;
  kicker: string;
  /** La carpeta del sitio, para el aviso del modo local. */
  repo: string;
  duration: string;
  notePlaceholder: string;
  counts: (c: PublishCounts, tn: Translator['tn']) => string;
  issueLink: (i: PublishIssue) => string | null;
}

const n = (v: number | undefined) => v ?? 0;

const SITES: Record<PublishSite, SiteConfig> = {
  mtx: {
    api: '/admin/publish',
    key: 'publish',
    kicker: tk('Sitio MTX'),
    repo: 'mtx-web',
    duration: tk('entre 3 y 6 minutos'),
    notePlaceholder: tk('Nota opcional (p. ej. «precios nuevos de la Rush»)'),
    counts: (c, tn) =>
      [
        tn(n(c.models), '{n} moto', '{n} motos'),
        tn(n(c.dealers), '{n} punto de venta', '{n} puntos de venta'),
        tn(n(c.photos), '{n} foto', '{n} fotos'),
        // Los PDF de la página de Servicios, solo si hay.
        ...(c.documents ? [tn(c.documents, '{n} PDF', '{n} PDF')] : []),
      ].join(' · '),
    issueLink: (i) => {
      if (i.modelId) return `/motos/${i.modelId}${SECTION[i.section] ? `?seccion=${SECTION[i.section]}` : ''}`;
      if (i.section === 'catalogo') return '/motos';
      if (i.section === 'puntos') return '/puntos-de-venta';
      if (i.section === 'textos') return '/textos';
      return null;
    },
  },
  'phelon-moore': {
    api: '/admin/pm/publish',
    key: 'pm-publish',
    kicker: tk('Landing'),
    repo: 'phelon-moore-web',
    duration: tk('unos 2 o 3 minutos'),
    notePlaceholder: tk('Nota opcional (p. ej. «hito de EICMA»)'),
    counts: (c, tn) =>
      [
        // Desde oct-2026 la landing no lleva noticias: solo se cuentan en las publicaciones de antes.
        ...(c.news ? [tn(n(c.news), '{n} noticia', '{n} noticias')] : []),
        // Las publicaciones de antes del 8-oct no traían la historia.
        ...(c.history ? [tn(c.history, '{n} hito', '{n} hitos')] : []),
        // La gama: desde el 18-oct, si hay motos en el dashboard (si no, la landing usa la de su código).
        ...(c.models ? [tn(c.models, '{n} moto', '{n} motos')] : []),
        tn(n(c.photos), '{n} foto', '{n} fotos'),
      ].join(' · '),
    issueLink: (i) => {
      if (i.newsId) return `/noticias/${i.newsId}`;
      if (i.historyId) return `/historia/${i.historyId}`;
      if (i.modelId) return `/motos/${i.modelId}`;
      if (i.section === 'noticias') return '/noticias';
      if (i.section === 'historia') return '/historia';
      if (i.section === 'gama') return '/motos';
      return null;
    },
  },
};

/** La nota de la publicación; en las vueltas atrás, qué versión restauró (con la hora de quien mira). */
function NoteOf({ p }: { p: Publication }) {
  const { t } = useT();
  if (p.rollbackOf) {
    const date = formatDate(p.rollbackOf.createdAt);
    return (
      <>
        <IconRotate width={13} style={{ verticalAlign: '-2px', marginRight: 6, color: 'var(--faint)' }} />
        {p.rollbackOf.note
          ? t('Vuelta a la versión del {date} («{note}»)', { date, note: p.rollbackOf.note })
          : t('Vuelta a la versión del {date}', { date })}
      </>
    );
  }
  return p.note ? <>{p.note}</> : <span className="faint">—</span>;
}


/** Minutos:segundos desde `since`, actualizándose cada segundo. */
function Elapsed({ since }: { since: string }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const s = Math.max(0, Math.round((now - new Date(since).getTime()) / 1000));
  return (
    <span className="mono">
      {Math.floor(s / 60)}:{String(s % 60).padStart(2, '0')}
    </span>
  );
}

export function Publish({ site = 'mtx' }: { site?: PublishSite }) {
  const { t, tn, tr } = useT();
  const cfg = SITES[site];
  const qc = useQueryClient();
  const { data: me } = useMe();
  const { toast, confirm } = useFeedback();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const status = useQuery({ queryKey: [cfg.key, 'status'], queryFn: () => get<PublishStatus>(cfg.api) });
  const history = useQuery({ queryKey: [cfg.key, 'history'], queryFn: () => get<{ items: Publication[] }>(`${cfg.api}/history?limit=20`) });

  const activeId = status.data?.active?.id;
  // Mientras hay una publicación en curso, se consulta su estado cada pocos segundos.
  const active = useQuery({
    queryKey: [cfg.key, 'publication', activeId],
    queryFn: async () => (await get<{ publication: Publication }>(`${cfg.api}/${activeId}`)).publication,
    enabled: !!activeId,
    refetchInterval: (q) => (q.state.data && q.state.data.status !== 'BUILDING' && q.state.data.status !== 'PENDING' ? false : 4000),
  });

  // Cuando termina, se avisa y se refresca todo.
  const announced = useRef<string | null>(null);
  useEffect(() => {
    const p = active.data;
    if (!p || p.status === 'BUILDING' || p.status === 'PENDING' || announced.current === p.id) return;
    announced.current = p.id;
    if (p.status === 'SUCCEEDED') toast(t('Listo: el sitio ya muestra los cambios'));
    else toast(p.error ?? t('La publicación falló'), 'error');
    void qc.invalidateQueries({ queryKey: [cfg.key] });
    void qc.invalidateQueries({ queryKey: ['overview'] });
  }, [active.data, qc, toast, cfg.key, t]);

  /** Vuelve a pedir el estado; se espera a que llegue para no dejar el botón activo con datos viejos. */
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: [cfg.key] }), qc.invalidateQueries({ queryKey: ['overview'] })]);

  const publish = async (force: boolean) => {
    setBusy(true);
    try {
      const { publication } = await post<{ publication: Publication }>(cfg.api, { note, force });
      setNote('');
      if (publication.status === 'SUCCEEDED') toast(t('Publicación registrada'));
      else toast(t('Publicando: el sitio se está reconstruyendo'));
      await refresh();
    } catch (e) {
      toast(e instanceof ApiError && e.status === 422 ? t('Hay cosas por completar antes de publicar') : errorMessage(e), 'error');
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const republish = async (p: Publication) => {
    const ok = await confirm(
      tr(
        '¿Volver a publicar la versión del {date}? El sitio vuelve a mostrar ese contenido. Lo que está en el dashboard no cambia: la próxima publicación vuelve a subir lo actual.',
        { date: <strong>{formatDate(p.createdAt)}</strong> },
      ),
      { confirmLabel: t('Volver a esta versión') },
    );
    if (!ok) return;
    try {
      await post(`${cfg.api}/${p.id}/republish`);
      toast(t('Volviendo a esa versión'));
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
    await refresh();
  };

  if (status.isPending) return <Spinner />;
  if (status.error || !status.data) return <div className="notice error">{errorMessage(status.error)}</div>;
  const s = status.data;
  const running = active.data && (active.data.status === 'BUILDING' || active.data.status === 'PENDING') ? active.data : s.active;
  const errors = s.issues.filter((i) => i.level === 'error');
  const warnings = s.issues.filter((i) => i.level === 'warning');

  return (
    <>
      <PageHead
        kicker={t(cfg.kicker)}
        title={t('Publicar')}
        description={t('Lo que cambias en el dashboard se ve en el sitio después de publicar. Publicar reconstruye el sitio con el contenido de ese momento; tarda unos minutos.')}
      >
        {s.siteUrl && (
          <a className="btn" href={s.siteUrl} target="_blank" rel="noreferrer">
            <IconExternal /> {t('Ver el sitio')}
          </a>
        )}
      </PageHead>

      {s.mode === 'off' && (
        <div className="notice error" style={{ marginBottom: 16 }}>
          <span>
            <strong>{t('Esta API no puede publicar.')}</strong>{' '}
            {tr('No tiene CodeBuild configurado ({variable}): publica desde el dashboard en AWS.', {
              variable: <code>{site === 'mtx' ? 'SITE_BUILD_PROJECT' : 'PM_SITE_BUILD_PROJECT'}</code>,
            })}
          </span>
        </div>
      )}
      {s.mode === 'local' && (
        <div className="notice" style={{ marginBottom: 16 }}>
          <span>
            <strong>{t('Modo local.')}</strong>{' '}
            {tr('Aquí no hay build: la publicación queda registrada y el sitio local la toma con {command} en {repo}.', {
              command: <code>npm run content:fetch</code>,
              repo: cfg.repo,
            })}
          </span>
        </div>
      )}

      <div className="stack">
        {running ? (
          <section className="panel panel-pad publish-running">
            <div className="spinner" aria-hidden="true" />
            <div>
              <h2 className="section-title">{t('Publicando…')}</h2>
              <p className="muted" style={{ margin: 0 }}>
                {tr('El sitio se está reconstruyendo con el contenido de ese momento ({elapsed}). Suele tardar {duration}; puedes seguir trabajando.', {
                  elapsed: <Elapsed since={running.startedAt ?? running.createdAt} />,
                  duration: t(cfg.duration),
                })}
                {running.createdBy && ` ${t('Lo inició {name}.', { name: running.createdBy })}`}
              </p>
            </div>
          </section>
        ) : (
          <section className="panel panel-pad stack">
            <div className="row between row-wrap" style={{ alignItems: 'flex-start' }}>
              <div>
                <h2 className="section-title">{s.upToDate ? t('El sitio está al día') : t('Hay cambios sin publicar')}</h2>
                <p className="muted" style={{ margin: 0 }}>
                  {s.current
                    ? s.current.createdBy
                      ? t('Última publicación: {date}, por {name}.', { date: formatDate(s.current.finishedAt ?? s.current.createdAt), name: s.current.createdBy })
                      : t('Última publicación: {date}.', { date: formatDate(s.current.finishedAt ?? s.current.createdAt) })
                    : t('Todavía no se ha publicado nada desde el dashboard.')}
                </p>
              </div>
              {s.upToDate ? <span className="badge ok">{t('Al día')}</span> : <span className="badge warn">{t('Cambios pendientes')}</span>}
            </div>

            {!s.upToDate && s.current && s.changes.length > 0 && (
              <ul className="changes">
                {s.changes.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            )}

            {errors.length > 0 && (
              <div className="notice error" style={{ display: 'block' }}>
                <strong style={{ display: 'block', marginBottom: 6 }}>{t('Para poder publicar, falta:')}</strong>
                <IssueList issues={errors} link={cfg.issueLink} />
              </div>
            )}
            {warnings.length > 0 && (
              <details className="notice warn" style={{ display: 'block' }}>
                <summary style={{ cursor: 'pointer' }}>{tn(warnings.length, '{n} aviso (no impiden publicar)', '{n} avisos (no impiden publicar)')}</summary>
                <div style={{ marginTop: 8 }}>
                  <IssueList issues={warnings} link={cfg.issueLink} />
                </div>
              </details>
            )}

            <div className="publish-go">
              <input className="input" placeholder={t(cfg.notePlaceholder)} value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
              {s.upToDate ? (
                <button className="btn" disabled={busy || !s.ready} onClick={() => void publish(true)} title={t('Útil después de cambiar el código del sitio')}>
                  <IconRotate /> {t('Publicar de todos modos')}
                </button>
              ) : (
                <button className="btn btn-primary" disabled={busy || !s.ready} onClick={() => void publish(false)}>
                  {busy ? t('Iniciando…') : t('Publicar ahora')}
                </button>
              )}
            </div>
            <p className="faint" style={{ margin: 0, fontSize: 13 }}>
              {t('Se publican {counts}.', { counts: cfg.counts(s.counts, tn) })}
            </p>
          </section>
        )}

        <section className="panel" style={{ overflowX: 'auto' }}>
          <div className="panel-pad" style={{ paddingBottom: 8 }}>
            <h2 className="section-title">{t('Historial')}</h2>
          </div>
          {history.isPending ? (
            <div className="panel-pad">
              <Spinner />
            </div>
          ) : !history.data?.items.length ? (
            <div className="empty-state">{t('Todavía no hay publicaciones.')}</div>
          ) : (
            <table className="table table-stack">
              <thead>
                <tr>
                  <th>{t('Fecha')}</th>
                  <th>{t('Estado')}</th>
                  <th>{t('Quién')}</th>
                  <th>{t('Nota')}</th>
                  <th>{t('Contenido')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {history.data.items.map((p) => (
                  <tr key={p.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {formatDate(p.createdAt)}
                      <div className="faint" style={{ fontSize: 12 }}>
                        {timeAgo(p.createdAt)}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${STATUS[p.status].tone}`}>
                        {p.status === 'SUCCEEDED' ? <IconCheck width={12} /> : p.status === 'FAILED' ? <IconAlert width={12} /> : null}
                        {t(STATUS[p.status].label)}
                      </span>
                      {p.id === s.current?.id && (
                        <span className="badge" style={{ marginLeft: 6 }}>
                          {t('En el sitio')}
                        </span>
                      )}
                    </td>
                    <td data-label={t('Quién')}>{p.createdBy ?? '—'}</td>
                    <td data-label={t('Nota')} style={{ maxWidth: 360 }}>
                      <NoteOf p={p} />
                      {p.error && (
                        <div style={{ color: '#ff8a8f', fontSize: 13, marginTop: 4 }}>
                          {p.error}
                          {p.logsUrl && (
                            <>
                              {' '}
                              <a href={p.logsUrl} target="_blank" rel="noreferrer">
                                {t('Ver registro')}
                              </a>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="faint" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                      {cfg.counts(p.counts, tn)}
                    </td>
                    <td className="cell-actions" style={{ textAlign: 'right' }}>
                      {me?.role === 'ADMIN' && p.restorable && !running && s.mode !== 'off' && (
                        <button className="btn btn-sm btn-ghost" onClick={() => void republish(p)}>
                          <IconRotate /> {t('Volver a esta versión')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </>
  );
}

function IssueList({ issues, link }: { issues: PublishIssue[]; link: (i: PublishIssue) => string | null }) {
  const { t } = useT();
  return (
    <ul className="issues">
      {issues.map((i, k) => {
        const to = link(i);
        const who = i.model ?? (i.news ? t('«{name}»', { name: i.news }) : i.history ? t('Historia, «{name}»', { name: i.history }) : null);
        const text = who ? `${who}: ${i.message}` : i.message;
        return (
          <li key={`${k}-${i.modelId ?? i.newsId ?? i.historyId ?? ''}${i.message}`}>
            <IconAlert className="ico" style={{ color: i.level === 'error' ? 'var(--red)' : 'var(--warn)' }} />
            {to ? <Link to={to}>{text}</Link> : <span>{text}</span>}
          </li>
        );
      })}
    </ul>
  );
}
